/**
 * A date picker that stores an ISO-8601 string.
 *
 * The content model types every date as `IsoDate`, a string, so that snapshots
 * diff cleanly in git (`packages/schema/src/types.ts`). FireCMS's built-in
 * `date` property writes a Firestore `Timestamp` instead, which serializes to
 * `{"_seconds": …}` and reaches the renderer as garbage. The exporter defends
 * against that by converting Timestamps on the way out, but a defence in the
 * last mile is not a reason to write the wrong type in the first place: a
 * Timestamp in Firestore is also invisible to `evaluatePublishGate`, which
 * calls `Date.parse` on a string and would report every date as missing.
 *
 * So the property stays `dataType: "string"` and this field supplies the
 * calendar widget. One conversion, at the only place a date is authored.
 */

import React from "react";
import { FieldHelperText, FieldProps } from "@firecms/core";
import { DateTimeField } from "@firecms/ui";

export interface IsoDateFieldProps {
    /** `date_time` keeps the time of day, which publish timestamps need. */
    mode?: "date" | "date_time";
}

export function IsoDateField({
                                 property,
                                 value,
                                 setValue,
                                 error,
                                 showError,
                                 disabled,
                                 includeDescription,
                                 customProps
                             }: FieldProps<string, IsoDateFieldProps>) {

    /* An unparseable string is shown as empty rather than as "Invalid Date", but
       it is left in the document untouched: silently overwriting a value the
       editor cannot see would lose data. The publish gate reports it. */
    const parsed = value ? new Date(value) : null;
    const dateValue = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;

    return (
        <>
            <DateTimeField
                value={dateValue}
                label={property.name}
                mode={customProps?.mode ?? "date_time"}
                onChange={(date: Date | null) => setValue(date ? date.toISOString() : null)}
                disabled={disabled ?? false}
                clearable={!property.validation?.required}
                error={Boolean(showError && error)}
                /* UTC, to match the stored value. A local-timezone widget would
                   show a different day than the string it just wrote. */
                timezone={"UTC"}
            />
            <FieldHelperText error={error}
                             showError={showError ?? false}
                             includeDescription={includeDescription ?? true}
                             property={property}/>
        </>
    );
}
